import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import headLogo from "../head/head.png";
import {
  CONFIRMATION_API_ENDPOINT,
  IDS_API_ENDPOINT,
} from "../../services/appsScript";
import "./styles.css";

const REQUEST_TIMEOUT_MS = 60000;
const MAX_REQUEST_ATTEMPTS = 2;
const RETRY_DELAY_MS = 5000;
const CONFIRMATION_CLOSES_AT = new Date("2026-10-21T00:01:00-06:00").getTime();

function shouldRetryRequest(error) {
  const status = error?.response?.status;

  return (
    error?.code === "ECONNABORTED" ||
    error?.code === "ERR_NETWORK" ||
    !error?.response ||
    status === 408 ||
    status === 429 ||
    status >= 500
  );
}

async function requestWithRetry(config) {
  for (let attempt = 1; attempt <= MAX_REQUEST_ATTEMPTS; attempt += 1) {
    try {
      return await axios({ ...config, timeout: REQUEST_TIMEOUT_MS });
    } catch (error) {
      const hasAnotherAttempt = attempt < MAX_REQUEST_ATTEMPTS;

      if (!hasAnotherAttempt || !shouldRetryRequest(error)) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }

  throw new Error("No se pudo completar la solicitud.");
}

function formatConfirmationDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("es-GT", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function normalizeGuestLimit(value) {
  const parsedValue = Number.parseInt(value, 10);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : 1;
}

export default function ConfirmationForm() {
  const [isClosed, setIsClosed] = useState(
    () => Date.now() >= CONFIRMATION_CLOSES_AT
  );
  const [idInput, setIdInput] = useState("");
  const [validatedId, setValidatedId] = useState("");
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [invitedData, setInvitedData] = useState({
    nombre: "",
    invitados: "",
  });
  const [existingConfirmation, setExistingConfirmation] = useState(null);

  const [attendance, setAttendance] = useState("sí");
  const [selectedGuestCount, setSelectedGuestCount] = useState(0);
  const [message, setMessage] = useState("");
  const [loadingGuest, setLoadingGuest] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    const timeUntilClosing = CONFIRMATION_CLOSES_AT - Date.now();

    if (timeUntilClosing <= 0) {
      setIsClosed(true);
      return undefined;
    }

    const closingTimer = window.setTimeout(() => {
      setIsClosed(true);
    }, timeUntilClosing);

    return () => window.clearTimeout(closingTimer);
  }, []);

  useEffect(() => {
    const fetchInvitedData = async () => {
      if (!validatedId) {
        return;
      }

      try {
        setLoadingGuest(true);
        setStatus({ type: "", message: "" });
        setExistingConfirmation(null);

        const guestResponse = await requestWithRetry({
          method: "get",
          url: IDS_API_ENDPOINT,
          params: { id: validatedId },
        });

        const guestData = guestResponse.data;

        if (!guestData.ok) {
          throw new Error("No se encontró información.");
        }

        const guestLimit = normalizeGuestLimit(guestData.invitados);

        setInvitedData({
          nombre: guestData.nombre || "",
          invitados: guestLimit,
        });
        setSelectedGuestCount(guestLimit);

        const confirmationResponse = await requestWithRetry({
          method: "get",
          url: CONFIRMATION_API_ENDPOINT,
          params: { id: validatedId },
        });

        const confirmationData = confirmationResponse.data;

        if (confirmationData?.ok === false) {
          throw new Error(
            confirmationData.error ||
              "No se pudo revisar si ya existe una confirmación."
          );
        }

        if (confirmationData?.found) {
          setExistingConfirmation(confirmationData);
          setStatus({
            type: "success",
            message: "Este código ya tiene una confirmación registrada.",
          });
        }
      } catch (error) {
        setInvitedData({
          nombre: "",
          invitados: "",
        });
        setSelectedGuestCount(0);
        setExistingConfirmation(null);

        setStatus({
          type: "error",
          message:
            error?.response?.data?.error ||
            error.message ||
            "No se pudo cargar la información del invitado.",
        });
      } finally {
        setLoadingGuest(false);
      }
    };

    fetchInvitedData();
  }, [validatedId, validationAttempt]);

  const handleIdSubmit = (e) => {
    e.preventDefault();

    if (isClosed) {
      setStatus({
        type: "error",
        message: "El período de confirmaciones ya cerró.",
      });
      return;
    }

    const nextId = idInput.trim();

    if (!nextId) {
      setStatus({
        type: "error",
        message: "Ingresa tu código de invitación.",
      });
      return;
    }

    setMessage("");
    setAttendance("sí");
    setInvitedData({
      nombre: "",
      invitados: "",
    });
    setSelectedGuestCount(0);
    setExistingConfirmation(null);
    setValidatedId(nextId);
    setValidationAttempt((attempt) => attempt + 1);
  };

  const handleIdChange = (e) => {
    setIdInput(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase());
  };

  const handleAttendanceChange = (e) => {
    const nextAttendance = e.target.value;

    setAttendance(nextAttendance);
    setSelectedGuestCount(
      nextAttendance === "no" ? 0 : normalizeGuestLimit(invitedData.invitados)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (isClosed) {
      setStatus({
        type: "error",
        message: "El período de confirmaciones ya cerró.",
      });
      return;
    }

    if (!validatedId || !invitedData.nombre) {
      setStatus({
        type: "error",
        message: "Primero valida tu código de invitación.",
      });
      return;
    }

    if (existingConfirmation) {
      setStatus({
        type: "error",
        message: "Este código ya tiene una confirmación registrada.",
      });
      return;
    }

    const confirmedGuestCount =
      attendance === "no" ? 0 : selectedGuestCount;

    try {
      setLoadingSubmit(true);

      const formData = new URLSearchParams({
        id: validatedId,
        nombre: invitedData.nombre,
        asistencia: attendance,
        invitados: String(confirmedGuestCount),
        mensaje: message,
      });

      const response = await requestWithRetry({
        method: "post",
        url: CONFIRMATION_API_ENDPOINT,
        data: formData,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const data = response.data;

      if (data?.ok === false) {
        throw new Error(data.error || "No se pudo enviar la confirmación.");
      }

      setStatus({
        type: "success",
        message: "Tu confirmación fue enviada correctamente.",
      });
      setExistingConfirmation({
        found: true,
        id: validatedId,
        nombre: invitedData.nombre,
        asistencia: attendance,
        invitados: confirmedGuestCount,
        mensaje: message,
      });

      setAttendance("sí");
      setMessage("");
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error?.response?.data?.error ||
          error.message ||
          "No se pudo enviar la confirmación. Intenta nuevamente.",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const isCurrentIdValidated = idInput.trim() === validatedId;
  const canShowForm =
    !isClosed &&
    isCurrentIdValidated &&
    !loadingGuest &&
    !!invitedData.nombre &&
    !existingConfirmation;
  const canShowExistingConfirmation =
    isCurrentIdValidated &&
    !loadingGuest &&
    !!invitedData.nombre &&
    !!existingConfirmation;
  const existingConfirmationDate = formatConfirmationDate(
    existingConfirmation?.fecha
  );
  const guestCountOptions = Array.from(
    { length: Number(invitedData.invitados) || 0 },
    (_, index) => invitedData.invitados - index
  );

  return (
    <main className="confirmation-page">
      <section className="confirmation-card">
        <img
          src={headLogo}
          alt="Boda Gálvez Barrios"
          className="confirmation-logo"
        />
        <h1 className="confirmation-title">Confirmación de asistencia</h1>
        <p className="confirmation-description">
          {isClosed
            ? "El período de confirmaciones finalizó el 20 de octubre."
            : "Completa este formulario para confirmar tu asistencia."}
        </p>

        <form className="id-validation-form" onSubmit={handleIdSubmit}>
          <div className="form-group">
            <label>Código de invitación</label>
            <input
              type="text"
              value={idInput}
              onChange={handleIdChange}
              pattern="[A-Za-z0-9]+"
              autoComplete="off"
              placeholder="Ingresa tu código"
              disabled={isClosed || loadingGuest || loadingSubmit}
            />
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isClosed || loadingGuest || loadingSubmit}
          >
            {isClosed
              ? "Confirmaciones cerradas"
              : loadingGuest
                ? "Validando..."
                : "Validar invitación"}
          </button>
        </form>

        {!isClosed && !validatedId && status.message && (
          <p className={`form-status ${status.type}`}>{status.message}</p>
        )}

        {isClosed ? (
          <div className="invalid-link-box">
            <p className="form-status error">
              La lista de invitados se encuentra cerrada.
            </p>
            <Link to="/" className="back-link">
              Volver a la invitación
            </Link>
          </div>
        ) : loadingGuest ? (
          <p className="form-status">Cargando información del invitado...</p>
        ) : isCurrentIdValidated && validatedId && !canShowForm ? (
          <div className="invalid-link-box">
            <p className={`form-status ${status.type || "error"}`}>
              {status.message || "No se pudo validar este id."}
            </p>
            <Link to="/" className="back-link">
              Volver a la invitación
            </Link>
          </div>
        ) : canShowExistingConfirmation ? (
          <div className="existing-confirmation-box">
            <p className="form-status success">
              Este código ya tiene una confirmación registrada.
            </p>

            <div className="confirmation-summary">
              <p>
                <strong>Nombre:</strong> {existingConfirmation.nombre}
              </p>
              <p>
                <strong>Asistencia:</strong> {existingConfirmation.asistencia}
              </p>
              {existingConfirmation.invitados !== undefined &&
                existingConfirmation.invitados !== "" && (
                  <p>
                    <strong>Invitados confirmados:</strong>{" "}
                    {existingConfirmation.invitados}
                  </p>
                )}
              {existingConfirmationDate && (
                <p>
                  <strong>Fecha:</strong> {existingConfirmationDate}
                </p>
              )}
              {existingConfirmation.mensaje && (
                <p>
                  <strong>Mensaje:</strong> {existingConfirmation.mensaje}
                </p>
              )}
            </div>

            <Link to="/" className="back-link">
              Volver a la invitación
            </Link>
          </div>
        ) : canShowForm ? (
          <form className="confirmation-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nombre del invitado</label>
              <input type="text" value={invitedData.nombre} readOnly />
            </div>

            <div className="form-group">
              <label>Cantidad de invitados que asistirán</label>
              <select
                value={selectedGuestCount}
                onChange={(e) => setSelectedGuestCount(Number(e.target.value))}
                disabled={attendance === "no"}
              >
                {attendance === "no" ? (
                  <option value={0}>0</option>
                ) : (
                  guestCountOptions.map((guestCount) => (
                    <option key={guestCount} value={guestCount}>
                      {guestCount}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="form-group">
              <label>¿Confirmas tu asistencia?</label>
              <select
                value={attendance}
                onChange={handleAttendanceChange}
              >
                <option value="sí">Sí asistiré</option>
                <option value="no">No podré asistir</option>
              </select>
            </div>

            <div className="form-group">
              <label>Mensaje para los novios</label>
              <textarea
                rows="4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escribe aquí tu mensaje"
              />
            </div>

            {status.message && (
              <p className={`form-status ${status.type}`}>{status.message}</p>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={loadingSubmit}
              >
                {loadingSubmit ? "Enviando..." : "Enviar confirmación"}
              </button>

              <Link to="/" className="back-link">
                Volver a la invitación
              </Link>
            </div>
          </form>
        ) : (
          <Link to="/" className="back-link">
            Volver a la invitación
          </Link>
        )}
      </section>
    </main>
  );
}
